import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, BookOpen, AlertCircle, Brain, Target, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const REPORT_ICONS = [AlertCircle, Target, ShieldAlert, Brain, BookOpen];

const ResearchPage = () => {
  const { t } = useLanguage();
  const lang = t('researchPage');
  const reports = lang.reports.map((r, idx) => ({ ...r, icon: REPORT_ICONS[idx] }));

  return (
    <div className="bg-zinc-950 grid-background min-h-screen text-zinc-100 pb-24">
      {/* Hero Section */}
      <section className="pt-24 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-bold tracking-tight mb-6"
        >
          {lang.headline} <span className="text-zinc-500">{lang.headlineHighlight}</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-lg md:text-xl text-zinc-400 leading-relaxed mb-10 text-balance"
        >
          {lang.subheadline}
        </motion.p>
      </section>

      {/* Research Entries */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-16">
        {reports.map((report, idx) => (
          <motion.article
            key={report.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5, delay: idx * 0.1 }}
            className={`relative p-8 md:p-10 rounded-2xl border ${
              idx === 0
                ? 'bg-zinc-900 border-zinc-700 shadow-2xl'
                : 'bg-zinc-900/50 border-zinc-800'
            }`}
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <report.icon className="w-6 h-6 text-zinc-400" />
              <span className="text-sm font-bold tracking-wider uppercase text-zinc-500">
                {report.source} • {report.date}
              </span>
            </div>

            <h2 className="text-2xl md:text-3xl font-semibold text-zinc-100 mb-8 max-w-3xl leading-snug">
              {report.title}
            </h2>

            {/* Structured Insights Grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <div className="space-y-6">
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-2">{lang.labels.coreArgument}</h4>
                  <p className="text-zinc-300 leading-relaxed">{report.coreArgument}</p>
                </div>
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-2">{lang.labels.keyInsight}</h4>
                  <p className="text-zinc-300 leading-relaxed">{report.keyInsight}</p>
                </div>
              </div>
              <div className="space-y-6">
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-rose-500/80 font-bold mb-2 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                    {lang.labels.primaryRisk}
                  </h4>
                  <p className="text-zinc-300 leading-relaxed">{report.primaryRisk}</p>
                </div>
              </div>
            </div>

            {/* Doora Response Panel */}
            <div className="relative p-6 rounded-xl bg-black border border-white/10 overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
              <h4 className="text-sm font-semibold text-zinc-100 mb-3 flex items-center gap-2 relative z-10">
                <div className="w-1.5 h-4 bg-zinc-400 rounded-sm"></div>
                {lang.labels.dooraDelivers}
              </h4>
              <ul className="text-zinc-400 leading-relaxed relative z-10 space-y-2 list-disc list-outside ml-4">
                {report.dooraResponse.map((point, i) => (
                  <li key={i}>{point}</li>
                ))}
              </ul>
            </div>
          </motion.article>
        ))}
      </section>

      {/* Synthesis / CTA */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto mt-24 text-center">
        <h3 className="text-2xl font-semibold mb-6">{lang.ctaHeadline}</h3>
        <p className="text-zinc-400 mb-10">
          {lang.ctaSubheadline}
        </p>
        <Link
          to="/product"
          className="inline-flex items-center justify-center gap-2 bg-white text-zinc-950 font-medium px-6 py-3 rounded-lg hover:bg-zinc-200 transition-colors"
        >
          {lang.ctaButton} <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </div>
  );
};

export default ResearchPage;
